import React, { useCallback, useMemo, useState } from "react";
import { Field } from "formik";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";

import InputField from "./InputField";

const isBlank = (value, { treatZeroAsBlank = true } = {}) =>
  value === undefined || value === null || value === "" || (treatZeroAsBlank && value === 0);

export const parseValue = (props, value) =>
  props.type === "number"
    ? value === "" || value === null
      ? props.step
        ? 0.0
        : 0
      : props.step
      ? parseFloat(value)
      : parseInt(value)
    : value;

const Input = props => {
  const [isFocused, setIsFocused] = useState(false);
  const { showNAButton = true, treatZeroAsBlank = true, ...restProps } = props;

  const inputProps = useMemo(() => ({ type: props.type, step: props.step }), [
    props.type,
    props.step,
  ]);

  const onChange = useCallback(
    setFieldValue => event => {
      const newValue = parseValue(inputProps, event.target.value);

      setFieldValue(props.name, newValue);

      if (props.onChange) {
        props.onChange(props.name, newValue);
      }
    },
    [inputProps, props.name, props.onChange]
  );

  return (
    <Field {...restProps}>
      {({ field, form, meta }) => {
        const textAreaProps = props.type == "text" ? { multiline: true, minRows: 3, maxRows: 25 } : {};
        const errorMessage = meta.error || form.errors[field.name];
        const error = (meta.touched || form.submitCount > 0) && errorMessage;

        if (!meta.touched && !field.value && props.value) {
          form.setFieldValue(field.name, props.value);
        }

        const isEmpty = isBlank(field.value, { treatZeroAsBlank });
        const showNABtn = showNAButton && isFocused && isEmpty && props.type !== "autoExpand";

        const handleNAClick = () => {
          form.setFieldValue(field.name, "N/A");
        };

        return (
          <InputField
            {...field}
            form={form}
            field={field}
            size="small"
            autoComplete="off"
            value={isEmpty ? "" : field.value}
            error={!!error}
            helperText={error}
            variant="outlined"
            {...restProps}
            showNAButton={showNAButton}
            margin="none"
            {...textAreaProps}
            onChange={onChange(form.setFieldValue)}
            onFocus={event => {
              setIsFocused(true);
              if (props.onFocus) props.onFocus(event);
            }}
            onBlur={event => {
              setIsFocused(false);
              if (props.onBlur) props.onBlur(event);
            }}
            InputProps={{
              ...restProps.InputProps,
              endAdornment: showNABtn ? (
                <InputAdornment position="end">
                  <Chip
                    label="N/A"
                    size="small"
                    clickable
                    color="primary"
                    variant="outlined"
                    onMouseDown={handleNAClick}
                    sx={{ cursor: "pointer" }}
                  />
                </InputAdornment>
              ) : restProps.InputProps?.endAdornment,
            }}
            inputProps={{ ...inputProps, ...props.inputProps }}
          />
        );
      }}
    </Field>
  );
};

export default React.memo(Input);
